import React, { useState, useEffect } from "react";
import { Container, Card, Form, Button, Row, Col, Spinner, Image } from "react-bootstrap";
import { FaSave, FaArrowLeft, FaUpload, FaTrash, FaTimes } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Header from "../../../components/common/Header";
import ProjectService from "../../../services/projectService";

const AdminAddProject = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ categories: [], languages: [] });

  // Form State
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [price, setPrice] = useState("");
  const [videoUrl, setVideoUrl] = useState("");
  const [demoUrl, setDemoUrl] = useState("");
  const [githubUrl, setGithubUrl] = useState("");
  const [zipFile, setZipFile] = useState(null);
  const [docxFile, setDocxFile] = useState(null);
  const [pdfFile, setPdfFile] = useState(null);
  const [thumbnail, setThumbnail] = useState(null);
  const [thumbnailPreview, setThumbnailPreview] = useState(null);
  
  // New Array States
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedLanguages, setSelectedLanguages] = useState([]);

  const [images, setImages] = useState([]); // Store actual files
  const [imagePreviews, setImagePreviews] = useState([]); // Store base64 previews

  useEffect(() => {
    loadFilters();
  }, []);

  const loadFilters = async () => {
    try {
      const res = await ProjectService.getProjectFilters();
      setFilters(res.data?.data || { categories: [], languages: [] });
    } catch (error) {
      toast.error("Failed to load filters");
    }
  };

  const handleCategoryChange = (e) => {
    const value = parseInt(e.target.value);
    if (e.target.checked) {
      setSelectedCategories([...selectedCategories, value]);
    } else {
      setSelectedCategories(selectedCategories.filter((id) => id !== value));
    }
  };

  const handleLanguageChange = (e) => {
    const value = parseInt(e.target.value);
    if (e.target.checked) setSelectedLanguages([...selectedLanguages, value]);
    else setSelectedLanguages(selectedLanguages.filter((id) => id !== value));
  };

  const handleThumbnailChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast.error("Thumbnail too large (Max 2MB)");
        return;
      }
      setThumbnail(file);
      const reader = new FileReader();
      reader.onloadend = () => setThumbnailPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    
    // Validate types and size
    const validFiles = files.filter(file => {
      if (!['image/jpeg', 'image/png', 'image/jpg', 'image/webp'].includes(file.type)) {
        toast.error(`Invalid file type: ${file.name}`);
        return false;
      }
      if (file.size > 2 * 1024 * 1024) {
        toast.error(`File too large: ${file.name} (Max 2MB)`);
        return false;
      }
      return true;
    });

    if (validFiles.length > 0) {
      setImages(prev => [...prev, ...validFiles]);
      
      // Generate previews
      validFiles.forEach(file => {
        const reader = new FileReader();
        reader.onloadend = () => {
          setImagePreviews(prev => [...prev, reader.result]);
        };
        reader.readAsDataURL(file);
      });
    }
  };

  const removeImage = (index) => {
    setImages(prev => prev.filter((_, i) => i !== index));
    setImagePreviews(prev => prev.filter((_, i) => i !== index));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    
    if (!title || !description || !price) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (!zipFile) {
      toast.error("Please upload the project ZIP file");
      return;
    }

    try {
      setLoading(true);
      const formData = new FormData();
      formData.append("title", title);
      formData.append("description", description);
      formData.append("price", price);
      formData.append("video_url", videoUrl);
      formData.append("demo_url", demoUrl);
      formData.append("github_url", githubUrl);
      formData.append("zip_file", zipFile);
      if (docxFile) formData.append("docx_file", docxFile);
      if (pdfFile) formData.append("pdf_file", pdfFile);
      if (thumbnail) {
        formData.append("thumbnail", thumbnail);
      }
      
      // Append array data (JSON stringified for PHP to decode)
      formData.append("categories", JSON.stringify(selectedCategories));
      formData.append("languages", JSON.stringify(selectedLanguages));

      // Append multiple images
      images.forEach((img) => {
        formData.append("images[]", img);
      });

      await ProjectService.adminCreateProject(formData);
      toast.success("Project created successfully");
      navigate("/admin/projects");
    } catch (error) {
      console.error(error);
      toast.error("Failed to create project");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-light min-vh-100">
      <Header />
      <Container className="py-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h4>Add New Project</h4>
          <Button variant="outline-secondary" onClick={() => navigate("/admin/projects")}>
            <FaArrowLeft className="me-2" /> Back to Projects
          </Button>
        </div>

        <Form onSubmit={handleSave}>
          <Row>
            <Col md={8}>
              <Card className="shadow-sm mb-4 border-0 rounded-4">
                <Card.Body className="p-4">
                  <h5 className="mb-4">Project Details</h5>
                  
                  <Form.Group className="mb-3">
                    <Form.Label>Project Title <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      required
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      placeholder="E.g., Complete E-commerce Platform"
                    />
                  </Form.Group>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Price (₹) <span className="text-danger">*</span></Form.Label>
                        <Form.Control
                          type="number"
                          required
                          value={price}
                          onChange={(e) => setPrice(e.target.value)}
                          placeholder="999"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Source Code (ZIP) <span className="text-danger">*</span></Form.Label>
                        <Form.Control
                          type="file"
                          accept=".zip"
                          required
                          onChange={(e) => setZipFile(e.target.files[0])}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Project Report (DOCX)</Form.Label>
                        <Form.Control
                          type="file"
                          accept=".doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                          onChange={(e) => setDocxFile(e.target.files[0])}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Project Report (PDF)</Form.Label>
                        <Form.Control
                          type="file"
                          accept=".pdf,application/pdf"
                          onChange={(e) => setPdfFile(e.target.files[0])}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>YouTube Demo URL</Form.Label>
                        <Form.Control
                          value={videoUrl}
                          onChange={(e) => setVideoUrl(e.target.value)}
                          placeholder="https://youtube.com/watch?v=..."
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Live Demo URL</Form.Label>
                        <Form.Control
                          value={demoUrl}
                          onChange={(e) => setDemoUrl(e.target.value)}
                          placeholder="https://myproject.com"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>GitHub URL</Form.Label>
                        <Form.Control
                          value={githubUrl}
                          onChange={(e) => setGithubUrl(e.target.value)}
                          placeholder="https://github.com/..."
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group className="mb-3">
                    <Form.Label>
                      Description (Raw HTML supported) <span className="text-danger">*</span>
                    </Form.Label>
                    <div className="bg-light p-2 rounded mb-2 small text-muted">
                      💡 You can paste HTML directly here (e.g., &lt;h3&gt;Features&lt;/h3&gt;, &lt;ul&gt;&lt;li&gt;...&lt;/li&gt;&lt;/ul&gt;)
                    </div>
                    <Form.Control
                      as="textarea"
                      rows={10}
                      required
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="<h1>Project Features</h1><p>This project includes...</p>"
                      style={{ fontFamily: 'monospace' }}
                    />
                  </Form.Group>
                </Card.Body>
              </Card>
            </Col>

            <Col md={4}>
              <Card className="shadow-sm mb-4 border-0 rounded-4">
                <Card.Body className="p-4">
                  <h5 className="mb-4">Taxonomy</h5>
                  
                  <Form.Group className="mb-4">
                    <Form.Label className="fw-bold text-muted small text-uppercase">Categories</Form.Label>
                    <div className="border p-3 rounded" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                      {filters.categories.map((cat) => (
                        <Form.Check key={cat.id} type="checkbox" id={`cat-${cat.id}`} label={cat.name} value={cat.id} checked={selectedCategories.includes(cat.id)} onChange={handleCategoryChange} />
                      ))}
                    </div>
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-bold text-muted small text-uppercase">Tech Stack</Form.Label>
                    <div className="border p-3 rounded" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                      {filters.languages.map((lang) => (
                        <Form.Check key={lang.id} type="checkbox" id={`lang-${lang.id}`} label={lang.name} value={lang.id} checked={selectedLanguages.includes(lang.id)} onChange={handleLanguageChange} />
                      ))}
                    </div>
                  </Form.Group>
                </Card.Body>
              </Card>
              <Card className="shadow-sm mb-4 border-0 rounded-4">
                <Card.Body className="p-4">
                  <h5 className="mb-3">Hero Image (Thumbnail)</h5>
                  <p className="small text-muted mb-3">This image will appear on project cards in the marketplace.</p>
                  
                  <Form.Group className="mb-3">
                    <Form.Control
                      type="file"
                      accept="image/jpeg,image/png,image/jpg,image/webp"
                      onChange={handleThumbnailChange}
                    />
                  </Form.Group>

                  {thumbnailPreview && (
                    <div className="position-relative mb-3" style={{ width: '100%', height: '160px' }}>
                      <Image src={thumbnailPreview} alt="thumbnail" rounded style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      <Button 
                        variant="danger" 
                        size="sm" 
                        className="position-absolute top-0 end-0 m-2"
                        onClick={() => { setThumbnail(null); setThumbnailPreview(null); }}
                      >
                        <FaTimes />
                      </Button>
                    </div>
                  )}
                </Card.Body>
              </Card>

              <Card className="shadow-sm mb-4 border-0 rounded-4">
                <Card.Body className="p-4">
                  <h5 className="mb-3">Project Images</h5>
                  <p className="small text-muted mb-3">Upload screenshots of the project (Max 2MB each). These will be uploaded to Cloudinary.</p>
                  
                  <Form.Group className="mb-3">
                    <Form.Control
                      type="file"
                      accept="image/jpeg,image/png,image/jpg,image/webp"
                      multiple
                      onChange={handleImageChange}
                    />
                  </Form.Group>

                  {imagePreviews.length > 0 && (
                    <div className="d-flex flex-wrap gap-2 mt-3">
                      {imagePreviews.map((preview, index) => (
                        <div key={index} className="position-relative" style={{ width: '80px', height: '80px' }}>
                          <Image src={preview} alt="preview" thumbnail style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          <Button 
                            variant="danger" 
                            size="sm" 
                            className="position-absolute top-0 end-0 p-0 rounded-circle d-flex justify-content-center align-items-center"
                            style={{ width: '20px', height: '20px', transform: 'translate(30%, -30%)' }}
                            onClick={() => removeImage(index)}
                          >
                            <FaTimes size={10} />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </Card.Body>
              </Card>

              <div className="d-grid gap-2 mt-4">
                <Button variant="primary" size="lg" type="submit" disabled={loading} className="py-3 rounded-3 shadow">
                  {loading ? (
                    <><Spinner size="sm" className="me-2" /> Uploading & Saving...</>
                  ) : (
                    <><FaSave className="me-2" /> Publish Project</>
                  )}
                </Button>
              </div>
            </Col>
          </Row>
        </Form>
      </Container>
    </div>
  );
};

export default AdminAddProject;
